import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  collectionGroup,
  onSnapshot
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  getStorage, 
  ref, 
  uploadString, 
  getDownloadURL,
  deleteObject 
} from 'firebase/storage';
import { Brand, Project, Variation, TextObject, ThumbnailConcept, GenerationParams } from '../types';

// Firebase configuration — fail fast if env vars are missing (never ship placeholder keys)
const firebaseConfig: Record<string, string> = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

for (const [key, value] of Object.entries(firebaseConfig)) {
  if (!value || value.startsWith('YOUR_')) {
    throw new Error(`Missing Firebase config: ${key}. Set the corresponding VITE_FIREBASE_* env variable.`);
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);
export const googleProvider = new GoogleAuthProvider();

// Subscription tiers: imported from shared/plans.ts (single source of truth for landing, app, functions)
export {
  SUBSCRIPTION_TIERS,
  ADDON_CREDITS_BY_TIER,
  normalizeSubscriptionTier
} from '../../shared/plans';
import type { SubscriptionTier } from '../types';

// Credit costs: image gen/edit consume credits. Idea generation does not consume; minimum to run = IMAGE_GENERATION.
export const CREDIT_COSTS = {
  CONCEPT_GENERATION: 0,
  IMAGE_GENERATION: 10,
  IMAGE_EDIT: 10,
  TEXT_EDIT: 0 // Free
} as const;

/** Remove keys with undefined values so Firestore accepts the payload. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Partial<T>;
}

// ============================================
// BRANDS - Subcollection under users
// Path: users/{userId}/brands/{brandId}
// ============================================

/**
 * Get all brands for a user
 */
export const getUserBrands = async (userId: string): Promise<Brand[]> => {
  const brandsRef = collection(db, 'users', userId, 'brands');
  const q = query(brandsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Brand));
};

/**
 * Get a single brand by ID
 */
export const getBrand = async (userId: string, brandId: string): Promise<Brand | null> => {
  const brandRef = doc(db, 'users', userId, 'brands', brandId);
  const snapshot = await getDoc(brandRef);
  
  if (!snapshot.exists()) return null;
  
  return {
    id: snapshot.id,
    ...snapshot.data()
  } as Brand;
};

/**
 * Create a new brand under a user
 */
export const createBrand = async (userId: string, brand: Omit<Brand, 'id'>): Promise<Brand> => {
  const brandsRef = collection(db, 'users', userId, 'brands');
  const newBrandRef = doc(brandsRef);
  
  const brandData = stripUndefined({
    ...brand,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  
  await setDoc(newBrandRef, brandData);
  
  return {
    id: newBrandRef.id,
    ...brand
  };
};

/**
 * Update an existing brand
 */
export const updateBrand = async (
  userId: string, 
  brandId: string, 
  data: Partial<Brand>
): Promise<void> => {
  const brandRef = doc(db, 'users', userId, 'brands', brandId);
  
  await updateDoc(brandRef, stripUndefined({
    ...data,
    updatedAt: serverTimestamp()
  }));
};

/**
 * Delete a brand and all its projects
 */
export const deleteBrand = async (userId: string, brandId: string): Promise<void> => {
  // First, delete all projects under this brand
  const projectsRef = collection(db, 'users', userId, 'brands', brandId, 'projects');
  const projectsSnapshot = await getDocs(projectsRef);
  
  const deletePromises = projectsSnapshot.docs.map(projectDoc => 
    deleteDoc(projectDoc.ref)
  );
  await Promise.all(deletePromises);
  
  // Then delete the brand itself
  const brandRef = doc(db, 'users', userId, 'brands', brandId);
  await deleteDoc(brandRef);
};

// ============================================
// PROJECTS - Subcollection under brands
// Path: users/{userId}/brands/{brandId}/projects/{projectId}
// ============================================

/**
 * Get all projects for a specific brand
 */
export const getBrandProjects = async (
  userId: string, 
  brandId: string
): Promise<Project[]> => {
  const projectsRef = collection(db, 'users', userId, 'brands', brandId, 'projects');
  const q = query(projectsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const rawProject = {
      id: doc.id,
      brandId,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toMillis?.() || doc.data().createdAt || Date.now()
    };
    // Migrate old format to new format if needed
    return migrateProjectIfNeeded(rawProject);
  });
};

/**
 * Get all projects across all brands for a user (for dashboard)
 */
export const getAllUserProjects = async (userId: string): Promise<Project[]> => {
  // First get all brands
  const brands = await getUserBrands(userId);
  
  // Then get projects for each brand
  const projectPromises = brands.map(brand => getBrandProjects(userId, brand.id));
  const projectArrays = await Promise.all(projectPromises);
  
  // Flatten and sort by createdAt
  return projectArrays
    .flat()
    .sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Get a single project by ID
 */
export const getProject = async (
  userId: string, 
  brandId: string, 
  projectId: string
): Promise<Project | null> => {
  const projectRef = doc(db, 'users', userId, 'brands', brandId, 'projects', projectId);
  const snapshot = await getDoc(projectRef);
  
  if (!snapshot.exists()) return null;
  
  const rawProject = {
    id: snapshot.id,
    brandId,
    ...snapshot.data(),
    createdAt: snapshot.data().createdAt?.toMillis?.() || snapshot.data().createdAt || Date.now()
  };
  // Migrate old format to new format if needed
  return migrateProjectIfNeeded(rawProject);
};

/**
 * Subscribe to project doc for real-time updates (e.g. generationStatus from Cloud Function).
 * Returns unsubscribe function.
 */
export const subscribeToProject = (
  userId: string,
  brandId: string,
  projectId: string,
  onUpdate: (project: Project | null) => void
): (() => void) => {
  const projectRef = doc(db, 'users', userId, 'brands', brandId, 'projects', projectId);
  return onSnapshot(projectRef, (snapshot) => {
    if (!snapshot.exists()) {
      onUpdate(null);
      return;
    }
    const data = snapshot.data();
    const rawProject = {
      id: snapshot.id,
      brandId,
      ...data,
      createdAt: data.createdAt?.toMillis?.() ?? data.createdAt ?? Date.now()
    };
    onUpdate(migrateProjectIfNeeded(rawProject));
  });
};

/**
 * Create a new project under a brand
 */
export const createProject = async (
  userId: string, 
  brandId: string, 
  project: Omit<Project, 'id' | 'brandId' | 'createdAt'>
): Promise<Project> => {
  const projectsRef = collection(db, 'users', userId, 'brands', brandId, 'projects');
  const newProjectRef = doc(projectsRef);
  
  const projectData = {
    ...project,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(newProjectRef, projectData);
  
  return {
    id: newProjectRef.id,
    brandId,
    ...project,
    createdAt: Date.now()
  };
};

/**
 * Update an existing project
 */
export const updateProject = async (
  userId: string, 
  brandId: string, 
  projectId: string, 
  data: Partial<Project>
): Promise<void> => {
  const projectRef = doc(db, 'users', userId, 'brands', brandId, 'projects', projectId);
  
  // Remove id and brandId from data if present (shouldn't be updated)
  const { id, brandId: _, ...updateData } = data as any;
  
  await updateDoc(projectRef, {
    ...updateData,
    updatedAt: serverTimestamp()
  });
};

/**
 * Delete a project
 */
export const deleteProject = async (
  userId: string, 
  brandId: string, 
  projectId: string
): Promise<void> => {
  const projectRef = doc(db, 'users', userId, 'brands', brandId, 'projects', projectId);
  await deleteDoc(projectRef);
};

// ============================================
// IDEAS - Subcollection under project
// Path: users/{userId}/brands/{brandId}/projects/{projectId}/ideas/{ideaId}
// ============================================

export interface SavedIdea extends ThumbnailConcept {
  createdAt: number;
  usedAt?: number;
}

/**
 * Save thumbnail concepts as ideas under a project. Uses merge so existing usedAt is not overwritten.
 */
export const saveIdeas = async (
  userId: string,
  brandId: string,
  projectId: string,
  concepts: ThumbnailConcept[]
): Promise<void> => {
  const ideasRef = collection(db, 'users', userId, 'brands', brandId, 'projects', projectId, 'ideas');
  const now = Date.now();
  await Promise.all(
    concepts.map((concept) => {
      const ideaRef = doc(ideasRef, concept.id);
      const payload: Omit<SavedIdea, 'usedAt'> = {
        ...concept,
        createdAt: now,
      };
      return setDoc(ideaRef, payload, { merge: true });
    })
  );
};

/**
 * Get all ideas for a project, ordered by createdAt desc. Includes usedAt when set.
 */
export const getProjectIdeas = async (
  userId: string,
  brandId: string,
  projectId: string
): Promise<SavedIdea[]> => {
  const ideasRef = collection(db, 'users', userId, 'brands', brandId, 'projects', projectId, 'ideas');
  const q = query(ideasRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    createdAt: (d.data().createdAt as number) ?? 0,
  })) as SavedIdea[];
};

/**
 * Mark a project idea as used (image generated from it). Only sets usedAt if not already set.
 */
export const markSavedIdeaAsUsed = async (
  userId: string,
  brandId: string,
  projectId: string,
  ideaId: string
): Promise<void> => {
  const ideaRef = doc(db, 'users', userId, 'brands', brandId, 'projects', projectId, 'ideas', ideaId);
  const snapshot = await getDoc(ideaRef);
  if (!snapshot.exists()) return;
  const data = snapshot.data();
  if (data?.usedAt != null) return;
  await updateDoc(ideaRef, {
    usedAt: Date.now(),
  });
};

// ============================================
// CHAT MESSAGES - Subcollection under project
// Path: users/{userId}/brands/{brandId}/projects/{projectId}/chatMessages/{messageId}
// ============================================

export interface StoredChatMessage {
  role: 'user' | 'model';
  text: string;
  imageUrl?: string;
  createdAt: ReturnType<typeof serverTimestamp> | number;
}

/**
 * Get all chat messages for a project, ordered by createdAt asc.
 */
export const getProjectChatMessages = async (
  userId: string,
  brandId: string,
  projectId: string
): Promise<{ role: 'user' | 'model'; text: string; image?: string }[]> => {
  const chatRef = collection(db, 'users', userId, 'brands', brandId, 'projects', projectId, 'chatMessages');
  const q = query(chatRef, orderBy('createdAt', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => {
    const data = d.data();
    return {
      role: data.role as 'user' | 'model',
      text: data.text ?? '',
      image: data.imageUrl ?? undefined,
    };
  });
};

/**
 * Save a chat message. If messageId is provided (e.g. for model message with image), uses that doc id; otherwise auto-generates.
 */
export const saveChatMessage = async (
  userId: string,
  brandId: string,
  projectId: string,
  message: { role: 'user' | 'model'; text: string; imageUrl?: string },
  messageId?: string
): Promise<void> => {
  const chatRef = collection(db, 'users', userId, 'brands', brandId, 'projects', projectId, 'chatMessages');
  const payload = {
    role: message.role,
    text: message.text,
    ...(message.imageUrl != null && { imageUrl: message.imageUrl }),
    createdAt: serverTimestamp(),
  };
  if (messageId) {
    await setDoc(doc(chatRef, messageId), payload);
  } else {
    await addDoc(chatRef, payload);
  }
};

/**
 * Update an existing chat message doc with an image URL (e.g. after upload completes).
 */
export const updateChatMessageImageUrl = async (
  userId: string,
  brandId: string,
  projectId: string,
  messageId: string,
  imageUrl: string
): Promise<void> => {
  const messageRef = doc(db, 'users', userId, 'brands', brandId, 'projects', projectId, 'chatMessages', messageId);
  await updateDoc(messageRef, { imageUrl });
};

// ============================================
// STORAGE - Firebase Storage for images
// Path: users/{userId}/projects/{projectId}/variations/{filename}
// Path: users/{userId}/projects/{projectId}/chat/{messageId}.png (chat images)
// ============================================

const UPLOAD_TIMEOUT_MS = 45000; // 45 seconds
const MAX_INLINE_SIZE = 800_000; // ~800KB safe limit for Firestore (leaves room for other fields)

/**
 * Compress a base64 image to JPEG at reduced quality.
 * Returns a smaller base64 data URL suitable for Firestore inline storage.
 */
export const compressImage = (base64Image: string, quality = 0.5, maxWidth = 800): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(base64Image); return; }
        ctx.drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/jpeg', quality);
        resolve(compressed);
      } catch { resolve(base64Image); }
    };
    img.onerror = () => resolve(base64Image); // on error, return original
    img.src = base64Image;
  });
};

/**
 * Upload a variation image to Firebase Storage and return the download URL.
 * Throws with a clear message if Storage is not enabled or upload times out.
 */
export const uploadVariationImage = async (
  userId: string,
  projectId: string,
  base64Image: string
): Promise<string> => {
  if (!base64Image || !base64Image.startsWith('data:')) {
    throw new Error('UPLOAD_FAILED');
  }

  const filename = `${Date.now()}_${Math.random().toString(36).substring(7)}.png`;
  const imageRef = ref(storage, `users/${userId}/projects/${projectId}/variations/${filename}`);

  const uploadWithTimeout = Promise.race([
    (async () => {
      await uploadString(imageRef, base64Image, 'data_url');
      return getDownloadURL(imageRef);
    })(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT')), UPLOAD_TIMEOUT_MS)
    )
  ]);

  try {
    return await uploadWithTimeout;
  } catch (err: unknown) {
    // Re-throw a generic code so caller can do fallback
    throw new Error('UPLOAD_FAILED');
  }
};

/**
 * Upload a chat message image to Firebase Storage and return the download URL.
 * Path: users/{userId}/projects/{projectId}/chat/{messageId}.png
 */
export const uploadChatImage = async (
  userId: string,
  projectId: string,
  messageId: string,
  base64Image: string
): Promise<string> => {
  if (!base64Image || !base64Image.startsWith('data:')) {
    throw new Error('UPLOAD_FAILED');
  }
  const imageRef = ref(storage, `users/${userId}/projects/${projectId}/chat/${messageId}.png`);
  const uploadWithTimeout = Promise.race([
    (async () => {
      await uploadString(imageRef, base64Image, 'data_url');
      return getDownloadURL(imageRef);
    })(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT')), UPLOAD_TIMEOUT_MS)
    ),
  ]);
  try {
    return await uploadWithTimeout;
  } catch {
    throw new Error('UPLOAD_FAILED');
  }
};

/** Max data URL length to send to callables; above this we upload to Storage. ~3MB base64. */
const LANDING_REF_MAX_DATA_URL_LENGTH = 3_000_000;

/**
 * Upload landing handoff ref image to Storage and return download URL.
 * Use when the data URL is too large for callable payloads.
 */
export const uploadLandingRefImage = async (
  userId: string,
  dataUrl: string
): Promise<string> => {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    throw new Error('UPLOAD_FAILED');
  }
  const ext = dataUrl.includes('image/png') ? 'png' : 'jpg';
  const filename = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
  const imageRef = ref(storage, `users/${userId}/landing-ref/${filename}`);
  const uploadWithTimeout = Promise.race([
    (async () => {
      await uploadString(imageRef, dataUrl, 'data_url');
      return getDownloadURL(imageRef);
    })(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT')), UPLOAD_TIMEOUT_MS)
    ),
  ]);
  try {
    return await uploadWithTimeout;
  } catch {
    throw new Error('UPLOAD_FAILED');
  }
};

export { LANDING_REF_MAX_DATA_URL_LENGTH };

/**
 * Delete a variation image from Firebase Storage.
 * Handles both full download URLs and storage paths.
 */
export const deleteVariationImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extract storage path from download URL if needed
    const pathMatch = imageUrl.match(/\/o\/(.+?)(\?|$)/);
    if (pathMatch) {
      const storagePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, storagePath);
      await deleteObject(imageRef);
    } else if (imageUrl.startsWith('gs://') || !imageUrl.startsWith('http')) {
      // gs:// URL or raw path
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    }
    // Ignore base64 strings and other non-storage URLs
  } catch (error) {
    // Ignore errors - image might not exist or be a base64 string
    console.warn('Could not delete image:', error);
  }
};

// ============================================
// MIGRATION HELPERS
// ============================================

/**
 * Check if a project uses the old data format (string[] variations)
 * and migrate it to the new format (Variation[] with per-variation texts)
 */
export const migrateProjectIfNeeded = (project: any): Project => {
  // Check if variations is old format (array of strings)
  if (project.variations && project.variations.length > 0 && typeof project.variations[0] === 'string') {
    // Migrate to new format
    const legacyTexts: TextObject[] = project.texts || [];
    const migratedVariations: Variation[] = project.variations.map((imageUrl: string, index: number) => ({
      id: `migrated_${index}_${Date.now()}`,
      imageUrl,
      // First variation gets the legacy texts, others get empty
      texts: index === 0 ? legacyTexts : [],
      createdAt: project.createdAt || Date.now()
    }));
    
    return {
      ...project,
      variations: migratedVariations,
      texts: undefined // Remove legacy field
    };
  }
  
  // Already in new format or empty
  return project as Project;
};

// ============================================
// CLOUD FUNCTIONS - Callables
// ============================================

/** Payload for startIdeaGeneration callable. Params must be serializable (no base64 images for wizard flow). */
export interface StartIdeaGenerationPayload {
  projectId: string;
  brandId: string;
  params: Omit<GenerationParams, 'secondPersonImage' | 'logoImage'> & { secondPersonImage?: string; logoImage?: string };
}

/** Call Cloud Function to generate ideas in the background. Returns when the function has been invoked (fire-and-forget from client). */
export const startIdeaGeneration = httpsCallable<StartIdeaGenerationPayload, void>(functions, 'startIdeaGeneration', { timeout: 300_000 });

/** Payload for generateThumbnailImage callable. Concept and params must be serializable. */
export interface GenerateThumbnailImagePayload {
  projectId: string;
  brandId: string;
  concept: ThumbnailConcept;
  params: Omit<GenerationParams, 'secondPersonImage' | 'logoImage'> & { secondPersonImage?: string; logoImage?: string };
  referenceImageUrl?: string;
  ideaId?: string;
}

export interface GenerateThumbnailImageResult {
  variationId: string;
  imageUrl: string;
}

/** Call Cloud Function to generate a thumbnail image in the background. Function writes new variation to Firestore. */
export const generateThumbnailImageCallable = httpsCallable<GenerateThumbnailImagePayload, GenerateThumbnailImageResult>(functions, 'generateThumbnailImage');

// ============================================
// NEW Callables — all Gemini calls now go through Cloud Functions
// ============================================

/** Payload for generateConceptsCallable. */
export interface GenerateConceptsPayload {
  projectId: string;
  brandId: string;
  params: Omit<GenerationParams, 'secondPersonImage' | 'logoImage'>;
  previousConceptTitles?: string[];
  referenceImageUrl?: string;
}

/** Generate thumbnail concepts via Cloud Function (replaces client-side Gemini). */
export const generateConceptsCallable = httpsCallable<GenerateConceptsPayload, ThumbnailConcept[]>(functions, 'generateConceptsCallable', { timeout: 300_000 });

/** Payload for editThumbnailImageCallable. */
export interface EditThumbnailImagePayload {
  projectId: string;
  brandId: string;
  imageUrl: string;
  instruction: string;
  params: Omit<GenerationParams, 'secondPersonImage' | 'logoImage'>;
}

export interface EditThumbnailImageResult {
  imageUrl: string;
}

/** Edit a thumbnail image via Cloud Function (replaces client-side Gemini). */
export const editThumbnailImageCallable = httpsCallable<EditThumbnailImagePayload, EditThumbnailImageResult>(functions, 'editThumbnailImageCallable');

/** Payload for adaptThumbnailToPlatformCallable. */
export interface AdaptThumbnailPayload {
  projectId: string;
  brandId: string;
  imageUrl: string;
  targetPlatform: string;
  params: Omit<GenerationParams, 'secondPersonImage' | 'logoImage'>;
}

export interface AdaptThumbnailResult {
  imageUrl: string;
}

/** Adapt a thumbnail to a different platform via Cloud Function (replaces client-side Gemini). */
export const adaptThumbnailToPlatformCallable = httpsCallable<AdaptThumbnailPayload, AdaptThumbnailResult>(functions, 'adaptThumbnailToPlatformCallable');

export interface CreatePayPlusPaymentLinkPayload {
  tier: Exclude<SubscriptionTier, 'free'>;
  annual?: boolean;
}

export interface CreatePayPlusPaymentLinkResult {
  paymentPageLink: string;
  pageRequestUid?: string;
}

/** Create a PayPlus hosted payment link for subscription upgrades. */
export const createPayPlusPaymentLinkCallable =
  httpsCallable<CreatePayPlusPaymentLinkPayload, CreatePayPlusPaymentLinkResult>(functions, 'createPayPlusPaymentLink');
